import { redactUrl } from "@badorbad/shared";
import * as Sentry from "@sentry/react";

/**
 * Browser error reporting. Imported for its side effect, first in `main.tsx`,
 * so an error thrown while the app is still starting is still reported.
 *
 * Presence of the DSN is the switch, matching the backend: production sets it,
 * development and stage do not, and there is no second flag to keep in sync.
 * The `VITE_` prefix is required — `vite.config.ts` loads nothing else, so a
 * name without it is simply absent here and reporting stays off.
 *
 * This DSN ships inside the bundle and is readable by anyone. That is how
 * browser error reporting works everywhere: a DSN only permits sending reports
 * in, never reading anything out.
 */
const dsn = import.meta.env.VITE_SENTRY_DSN_FE as string | undefined;

if (dsn && dsn.trim() !== "") {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    beforeSend(event) {
      if (event.request?.url) {
        event.request.url = redactUrl(event.request.url);
      }
      for (const crumb of event.breadcrumbs ?? []) {
        const url = crumb.data?.url;
        if (typeof url === "string" && crumb.data) {
          crumb.data.url = redactUrl(url);
        }
      }
      return event;
    },
    /*
     * Off deliberately. The default already excludes IP addresses and cookies,
     * and the privacy page says we hold nothing about a guest beyond a random
     * identifier — turning this on would quietly make that untrue.
     */
    sendDefaultPii: false,
    /*
     * Errors only. No performance tracing and no session replay: both sample
     * ordinary play, and replay in particular would record the decisions people
     * write about each other — exactly the room-only content the plan promises
     * never leaves the room.
     */
    tracesSampleRate: 0,
  });
}
